import React, { useRef } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@dashai/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@dashai/components/ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  LabelList,
} from 'recharts';
import { ChartData } from '@dashai/types/dashai';
import html2canvas from 'html2canvas-pro';
import { formatNumber } from '@dashai/lib/utils';
import { trackDownload } from '@dashai/utils/trackDownload';

interface ChatChartProps {
  data: ChartData;
  embedded?: boolean; // For use inside deck canvas without card wrapper
  colorOverride?: string; // Override primary bar/series color (e.g., for Red Bull branding)
}

const COLORS = [
  'hsl(217, 91%, 60%)',
  'hsl(142, 76%, 36%)',
  'hsl(45, 93%, 47%)',
  'hsl(0, 84%, 60%)',
  'hsl(280, 87%, 65%)',
  'hsl(180, 70%, 45%)',
  'hsl(320, 70%, 50%)',
  'hsl(30, 90%, 55%)',
];

// Benchmark-specific colors: blue for franchise, red for competitors
const FRANCHISE_COLOR = '#3b82f6';
const COMPETITOR_COLOR = '#ef4444';

/** Brand / company color map — keyed by lowercase name for case-insensitive lookup */
const BRAND_COLORS: Record<string, string> = {
  // Companies
  "coca-cola": "#F40009",
  "pepsico": "#004B93",
  "red bull": "#003399",
  "heineken nv": "#00A650",
  "suntory": "#005BAC",
  "bacardi": "#1E1E1E",
  "monster beverage": "#95C11F",
  "nestlé": "#7B868C",
  "nestle": "#7B868C",
  "unilever": "#1F36C7",
  "keurig dr pepper": "#7B2D8E",
  "dr oetker": "#C8102E",
  // Brands
  "fanta": "#FF8300",
  "sprite": "#008B47",
  "fuze tea": "#A67B5B",
  "lipton": "#FFD100",
  "pepsi": "#004B93",
  "7up": "#006B3F",
  "monster": "#95C11F",
  "minute maid": "#FDB913",
  "capri sun": "#FF6600",
  "aquarius": "#00B2E3",
  "heineken": "#00A650",
  "fernandes": "#8B4513",
  "sisi": "#FFB347",
  "royal club": "#1B3A5C",
  "rockstar": "#D4AF37",
  "jack daniels & coca-cola": "#A0522D",
  "jack daniel's": "#A0522D",
  "absolut vodka & sprite": "#1C1C3B",
  "orangina": "#F5A623",
  "topo chico": "#00B5AD",
  "mountain dew": "#007A33",
  "gatorade": "#F77F00",
  "schweppes": "#C5A900",
  "dr pepper": "#7B2D8E",
  "costa coffee": "#6B1A2A",
  "powerade": "#0046AD",
  "smartwater": "#69B3E7",
  "innocent": "#8DC63F",
  "vita coco": "#00A86B",
};

/** Look up a brand color by name (case-insensitive). Returns undefined if not found. */
export function getBrandColor(name: string): string | undefined {
  return BRAND_COLORS[name.toLowerCase().trim()];
}

/**
 * Detect a percentage-based chart (rates, exclusivity, coverage, …) from its
 * title and y key. Titles are natural language (often the user's question), so
 * keyword checks must be whole-word: a substring match turns "show sepaRATEly"
 * into a percent axis and renders outlet counts as "229902.0%". yKeys are
 * snake_case identifiers, so their keywords match segments between separators.
 */
export function detectPercentageChart(title: string | undefined, yKey: string | undefined): boolean {
  const titleHasPercentWord = /\b(rate|rates|percent|percentage|exclusivity|coverage)\b/.test(
    title?.toLowerCase() ?? '',
  );
  const yKeySegments = (yKey?.toLowerCase() ?? '').split(/[^a-z0-9]+/);
  const yKeyHasPercentWord = yKeySegments.some(s =>
    ['rate', 'rates', 'pct', 'percent', 'percentage', 'coverage'].includes(s),
  );
  return titleHasPercentWord || yKeyHasPercentWord;
}

export function ChatChart({ data, embedded = false, colorOverride }: ChatChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);

  // Helper to format large numbers with K/M suffix or commas
  const formatLargeNumber = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return value.toLocaleString();
    }
    return value.toString();
  };

  const handleDownload = async () => {
    if (chartRef.current) {
      const canvas = await html2canvas(chartRef.current);
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      const fileName = `${data.title || 'chart'}.png`;
      link.download = fileName;
      link.href = url;
      link.click();
      trackDownload({ downloadType: 'png', source: 'chat-chart', fileName });
    }
  };

  const chartHeight = embedded ? '100%' : 300;

  // Support both modern keys (xKey/yKey) and legacy keys (xAxisKey/dataKey) from edge functions
  const yKeyResolved = data.yKey ?? data.dataKey ?? 'value';

  // Detect if this is a growth/percentage chart based on title
  const isGrowthChart = data.title?.toLowerCase().includes('growth') && data.title?.toLowerCase().includes('comparison');

  const isPercentageChart = detectPercentageChart(data.title, yKeyResolved) || isGrowthChart;

  const allData = data.data ?? [];

  // Detect if percentage values are 0-1 decimals (need *100) vs 0-100 (use as-is)
  // Use backend hint when available, fall back to value-range heuristic
  const isDecimalPercentage = isPercentageChart && allData.length > 0 && (
    data.percentageRange
      ? data.percentageRange === '0-1'
      : (() => {
          const keys = data.series && data.series.length > 1
            ? data.series
            : [yKeyResolved];
          const numericValues = allData
            .flatMap(d => keys.map(k => Number(d[k])))
            .filter(v => !isNaN(v) && v !== 0);
          return numericValues.length > 0 && numericValues.every(v => Math.abs(v) <= 1);
        })()
  );

  // Format a percentage value: handles both 0-1 decimal and 0-100 ranges
  const formatPercentValue = (value: number): string => {
    const displayValue = isDecimalPercentage ? value * 100 : value;
    if (isGrowthChart) {
      return `${displayValue > 0 ? '+' : ''}${displayValue.toFixed(1)}%`;
    }
    return `${displayValue.toFixed(1)}%`;
  };

  // Format value for labels
  const formatLabelValue = (value: number) => {
    if (isPercentageChart) return formatPercentValue(value);
    return formatNumber(value);
  };

  /** Resolve color for a series by index: use data.colors if provided, else default palette */
  const getSeriesColor = (index: number, seriesName?: string): string => {
    // colorOverride replaces the primary (first) color
    if (colorOverride && index === 0) return colorOverride;
    // If explicit colors array is provided (e.g., from brand color mapping), use it
    if (data.colors && data.colors[index]) return data.colors[index];
    // Try brand color lookup by series name
    if (seriesName) {
      const bc = getBrandColor(seriesName);
      if (bc) return bc;
    }
    return COLORS[index % COLORS.length];
  };

  const renderChart = () => {
    const seriesData = data.data ?? [];
    // Support both modern keys (xKey/yKey) and legacy keys (xAxisKey/dataKey) from edge functions
    const xKeyResolved = data.xKey ?? data.xAxisKey ?? 'name';

    switch (data.type) {
      case 'metric': {
        // Single metric display - large number with label
        const numericValue = typeof data.value === 'string' ? parseFloat(data.value) : data.value;
        return (
          <div className="flex flex-col items-center justify-center h-full py-8 min-h-[120px]">
            <div className="text-4xl md:text-5xl font-bold text-foreground">
              {numericValue !== undefined && !isNaN(numericValue)
                ? formatLargeNumber(numericValue)
                : data.value || '—'}
            </div>
            <div className="text-sm text-muted-foreground mt-2 text-center max-w-[200px]">
              {data.label || data.title}
            </div>
          </div>
        );
      }

      case 'bar': {
        // Multi-series: x = time (name), one Bar per series (top 5), grouped by x
        const barSeries = data.series && data.series.length > 1 ? data.series : [yKeyResolved];
        const barMargin = { top: 20, right: 10, left: data.yAxisLabel ? 50 : 0, bottom: 5 };
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart accessibilityLayer data={seriesData} margin={barMargin} barCategoryGap="12%">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey={xKeyResolved}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                interval={0}
                angle={seriesData.length > 6 ? -15 : 0}
                textAnchor={seriesData.length > 6 ? 'end' : 'middle'}
                height={40}
              />
              <YAxis
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                width={55}
                tickFormatter={(value) => (isPercentageChart ? formatPercentValue(value) : formatNumber(value))}
                label={data.yAxisLabel ? { value: data.yAxisLabel, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: 'hsl(var(--muted-foreground))', fontSize: 11 } } : undefined}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: 'hsl(var(--foreground))',
                }}
                formatter={(value: number, name: string) => [
                  isPercentageChart ? formatPercentValue(value) : value.toLocaleString(),
                  name,
                ]}
              />
              {barSeries.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
              {barSeries.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={key}
                  fill={getSeriesColor(index, key)}
                  radius={[4, 4, 0, 0]}
                  stackId={undefined}
                >
                  <LabelList
                    dataKey={key}
                    position="top"
                    fill="hsl(var(--foreground))"
                    fontSize={9}
                    fontWeight={600}
                    formatter={(v: number) => (v != null && v !== 0 ? formatLabelValue(v) : '')}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case 'horizontal-bar':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart accessibilityLayer data={seriesData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                type="number"
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                tickFormatter={(value) => isPercentageChart ? formatPercentValue(value) : formatNumber(value)}
              />
              <YAxis
                type="category"
                dataKey={xKeyResolved}
                width={80}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: 'hsl(var(--foreground))',
                }}
                formatter={(value: number) => [
                  isPercentageChart ? formatPercentValue(value) : value.toLocaleString(),
                  yKeyResolved,
                ]}
              />
              <Bar dataKey={yKeyResolved} fill={colorOverride || COLORS[0]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <PieChart accessibilityLayer>
              <Pie
                data={seriesData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={embedded ? undefined : ({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={embedded ? 60 : 100}
                dataKey="value"
              >
                {seriesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getSeriesColor(index, String(entry.name ?? ''))} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: 'hsl(var(--foreground))',
                }}
              />
              <Legend wrapperStyle={{ fontSize: embedded ? 11 : 14 }} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'line': {
        // Detect series: either from explicit series prop or by finding all numeric keys
        const firstPoint = seriesData[0] as Record<string, any> | undefined;
        const seriesKeys =
          data.series ||
          Object.keys(firstPoint || {}).filter((k) => k !== xKeyResolved && typeof firstPoint?.[k] === 'number');

        // Fallback to single 'value' if no series detected
        const effectiveSeries = seriesKeys.length > 0 ? seriesKeys : ['value'];

        const lineMargin = { top: 5, right: 30, left: data.yAxisLabel ? 55 : 10, bottom: seriesData.length > 8 ? 20 : 5 };
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart accessibilityLayer data={seriesData} margin={lineMargin}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey={xKeyResolved}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: embedded ? 10 : 12 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                interval={0}
                angle={seriesData.length > 8 ? -30 : 0}
                textAnchor={seriesData.length > 8 ? 'end' : 'middle'}
                height={seriesData.length > 8 ? 55 : 35}
              />
              <YAxis
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: embedded ? 10 : 12 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                tickFormatter={(value) => isPercentageChart ? formatPercentValue(value) : formatNumber(value)}
                width={55}
                label={data.yAxisLabel ? { value: data.yAxisLabel, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: 'hsl(var(--muted-foreground))', fontSize: 11 } } : undefined}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: 'hsl(var(--foreground))',
                }}
                formatter={(value: number, name: string) => [
                  isPercentageChart ? formatPercentValue(value) : value.toLocaleString(),
                  name,
                ]}
              />
              <Legend
                wrapperStyle={{
                  paddingTop: '10px',
                  fontSize: embedded ? 10 : 12,
                }}
              />
              {effectiveSeries.map((key, index) => {
                const color = getSeriesColor(index, key);
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={key}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ fill: color, r: embedded ? 2 : 3 }}
                    activeDot={{ r: embedded ? 4 : 6 }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        );
      }

      default:
        return null;
    }
  };

  // Embedded mode: just render the chart without card wrapper
  if (embedded) {
    return (
      <div ref={chartRef} className="w-full h-full">
        {renderChart()}
      </div>
    );
  }

  // For metric type, render a simpler card without download button
  if (data.type === 'metric') {
    return (
      <Card className="bg-card/50 border-border" ref={chartRef}>
        <CardContent className="p-4">
          {renderChart()}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 border-border" ref={chartRef}>
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-sm font-medium">{data.title}</CardTitle>
        <Button variant="ghost" size="icon" onClick={handleDownload} className="h-8 w-8">
          <Download className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="pb-4">
        {renderChart()}
      </CardContent>
    </Card>
  );
}
